import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, status: "", amount: 0 });
  const { clear } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sid) { setState({ loading: false, paid: false, status: "no-session", amount: 0 }); return; }
    let cancel = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (cancel) return;
        if (data.payment_status === "paid") {
          setState({ loading: false, paid: true, status: data.status, amount: data.amount });
          clear();
          return;
        }
        if (data.status === "expired" || attempts.current >= 8) {
          setState({ loading: false, paid: false, status: data.status, amount: data.amount });
          return;
        }
        attempts.current += 1;
        setTimeout(poll, 2000);
      } catch {
        if (!cancel) setState({ loading: false, paid: false, status: "error", amount: 0 });
      }
    };
    poll();
    return () => { cancel = true; };
  }, [sid, clear]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="checkout-success-page">
      {state.loading && (
        <>
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#002855]" />
          <h1 className="mt-6 font-display text-3xl text-[#0A192F]">Confirming your order…</h1>
          <p className="mt-2 text-slate-500">Please wait while we verify your payment.</p>
        </>
      )}
      {!state.loading && state.paid && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#0A192F]" />
          <div className="label-eyebrow mt-6 text-[#C5A059]">Order confirmed</div>
          <h1 className="mt-3 font-display text-4xl text-[#0A192F]">Thank you for supporting the Foundation.</h1>
          <p className="mt-4 text-slate-600">A receipt has been emailed by Stripe. Items typically ship within 5–7 business days.</p>
          <div className="mt-6 inline-block card-flat px-6 py-3 font-mono text-[#0A192F]" data-testid="success-amount">Total: ${state.amount?.toFixed(2)}</div>
          <div className="mt-10 flex gap-3 justify-center">
            <Link to="/store"><Button variant="outline" className="border-slate-200 rounded-md">Continue shopping</Button></Link>
            <Link to="/feed"><Button className="bg-[#002855] hover:bg-[#0A192F] text-white rounded-md">Back to the feed</Button></Link>
          </div>
        </>
      )}
      {!state.loading && !state.paid && (
        <>
          <XCircle className="w-14 h-14 mx-auto text-[#9B111E]" />
          <h1 className="mt-6 font-display text-3xl text-[#0A192F]">Payment not completed</h1>
          <p className="mt-2 text-slate-500">Status: {state.status || "unknown"}</p>
          <Link to="/cart"><Button className="mt-6 bg-[#002855] hover:bg-[#0A192F] text-white rounded-md">Return to cart</Button></Link>
        </>
      )}
    </div>
  );
}
